import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { SearchContentType, SearchResult, SearchStatus } from '@almanac/contracts';
import { createPortal } from 'react-dom';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMe } from '../api/MeContext';
import { useApi } from '../api/useApi';
import { useAuth } from '../auth/AuthContext';
import { askAlmanacAssistant } from '../lib/ask-assistant';
import {
  activeFilterCount,
  EMPTY_SEARCH_FILTERS,
  rankCandidate,
  type SearchFilters,
} from '../lib/search';
import { AlmanacLogo } from './AlmanacLogo';
import { useAssistant } from './assistant/AssistantProvider';
import { GlobalInputsLayout } from './inputs/GlobalInputsChrome';
import { GlobalInputsProvider } from './inputs/InputsContext';

export interface LocalSearchEntry {
  id: string;
  label: string;
  description: string;
  keywords: string;
  href: string;
}

interface SectionOption {
  id: string;
  label: string;
}

interface SearchContextValue {
  query: string;
  filters: SearchFilters;
  setQuery: (query: string) => void;
  setFilters: (filters: SearchFilters) => void;
  openSearch: () => void;
  setLocalEntries: (entries: LocalSearchEntry[]) => void;
  setSectionOptions: (sections: SectionOption[]) => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

export function useAlmanacSearch(): SearchContextValue {
  const value = useContext(SearchContext);
  if (!value) throw new Error('useAlmanacSearch must be used inside AppShell');
  return value;
}

function listParam(params: URLSearchParams, name: string): string[] {
  return params.get(name)?.split(',').filter(Boolean) ?? [];
}

function filtersFromParams(params: URLSearchParams): SearchFilters {
  return {
    sources: listParam(params, 'source'),
    sections: listParam(params, 'sectionFilter'),
    statuses: listParam(params, 'status') as SearchStatus[],
    contentTypes: listParam(params, 'type') as SearchContentType[],
    dateFrom: params.get('from'),
    dateTo: params.get('to'),
  };
}

function writeFilters(params: URLSearchParams, filters: SearchFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  const values: Array<[string, string[]]> = [
    ['source', filters.sources],
    ['sectionFilter', filters.sections],
    ['status', filters.statuses],
    ['type', filters.contentTypes],
  ];
  for (const [name, value] of values) {
    if (value.length > 0) next.set(name, value.join(','));
    else next.delete(name);
  }
  if (filters.dateFrom) next.set('from', filters.dateFrom);
  else next.delete('from');
  if (filters.dateTo) next.set('to', filters.dateTo);
  else next.delete('to');
  return next;
}

export function AppShell() {
  const { logout } = useAuth();
  const assistant = useAssistant();
  const [params, setParams] = useSearchParams();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [localEntries, setLocalEntries] = useState<LocalSearchEntry[]>([]);
  const [sectionOptions, setSectionOptions] = useState<SectionOption[]>([]);
  const location = useLocation();
  const query = params.get('filterQ') ?? '';
  const filters = useMemo(() => filtersFromParams(params), [params]);

  const setQuery = useCallback(
    (value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set('filterQ', value);
      else next.delete('filterQ');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const setFilters = useCallback(
    (value: SearchFilters) => setParams(writeFilters(params, value), { replace: true }),
    [params, setParams],
  );
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/u.test(target.tagName))
      ) {
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase('en-US') === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Publish the header's live height so page-level sticky bars (e.g. the
  // digest day header) can sit flush beneath it. The header grows when the
  // filter bar opens, so a hardcoded offset leaves a gap
  // that scrolled content bleeds through. Layout effect, not a passive one:
  // a URL-driven filter renders the taller header on the very first paint, so
  // a post-paint measurement would flash the CSS fallback offset first.
  const headerRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const root = document.documentElement;
    const sync = () => {
      root.style.setProperty('--app-header-height', `${header.getBoundingClientRect().height}px`);
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(header);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--app-header-height');
    };
  }, []);

  const context = useMemo<SearchContextValue>(
    () => ({
      query,
      filters,
      setQuery,
      setFilters,
      openSearch,
      setLocalEntries,
      setSectionOptions,
    }),
    [filters, openSearch, query, setFilters, setQuery],
  );

  return (
    <SearchContext.Provider value={context}>
      <GlobalInputsProvider>
        <GlobalInputsLayout>
          <div
            className={
              assistant.open
                ? 'isolate min-h-dvh overflow-x-clip bg-background text-foreground antialiased lg:pr-[26rem]'
                : 'isolate min-h-dvh overflow-x-clip bg-background text-foreground antialiased'
            }
          >
            <header
              ref={headerRef}
              className="sticky top-0 z-20 border-b border-border/60 bg-background/90 backdrop-blur"
            >
              <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3 sm:px-6">
                <Link to="/" aria-label="Homepage" className="shrink-0">
                  <AlmanacLogo compact />
                </Link>
                <nav className="flex items-center gap-1 max-lg:hidden" aria-label="Primary">
                  <Link
                    to="/today"
                    className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    Today
                  </Link>
                  <Link
                    to="/briefing"
                    className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    Eng. Features
                  </Link>
                  <Link
                    to="/docs"
                    className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    Docs
                  </Link>
                  <Link
                    to="/inputs"
                    className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    Inputs
                  </Link>
                  <Link
                    to="/capture"
                    className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    Inbox
                  </Link>
                  <Link
                    to="/research"
                    className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    Research
                  </Link>
                  <Link
                    to="/podcast"
                    className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    Podcast
                  </Link>
                </nav>
                <div className="flex min-w-0 flex-1 items-center justify-end gap-1">
                  <Link
                    to="/notifications"
                    aria-label="Notification settings"
                    className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <svg
                      aria-hidden="true"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
                    </svg>
                  </Link>
                  <button
                    type="button"
                    onClick={openSearch}
                    aria-label="Search Almanac or ask a question"
                    className="flex min-w-0 items-center gap-2 rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    <SearchIcon />
                    <span className="max-w-44 truncate max-sm:hidden">{query || 'Search'}</span>
                    <kbd className="shrink-0 rounded border border-border/70 bg-muted px-1.5 py-0.5 font-mono text-[0.6875rem] text-muted-foreground max-sm:hidden">
                      ⌘K
                    </kbd>
                  </button>
                  <div className="max-lg:hidden">
                    <AccountMenu logout={logout} />
                  </div>
                </div>
                <button
                  type="button"
                  aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-navigation"
                  onClick={() => setMobileMenuOpen((open) => !open)}
                  className="relative rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:hidden"
                >
                  {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                  <span
                    className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
                    aria-hidden="true"
                  />
                </button>
              </div>
              <ActiveFilters filters={filters} setFilters={setFilters} openSearch={openSearch} />
            </header>
            <MobileNavigation open={mobileMenuOpen} logout={logout} onClose={closeMobileMenu} />
            <Outlet />
            <CommandPalette
              open={searchOpen}
              onClose={() => setSearchOpen(false)}
              query={query}
              setQuery={setQuery}
              filters={filters}
              setFilters={setFilters}
              localEntries={localEntries}
              sectionOptions={sectionOptions}
            />
          </div>
        </GlobalInputsLayout>
      </GlobalInputsProvider>
    </SearchContext.Provider>
  );
}

const mobileNavigationLinks = [
  { href: '/today', label: 'Today' },
  { href: '/briefing', label: 'Eng. Features' },
  { href: '/docs', label: 'Docs' },
  { href: '/inputs', label: 'Inputs' },
  { href: '/research', label: 'Research' },
  { href: '/capture', label: 'Inbox' },
  { href: '/podcast', label: 'Podcast' },
  { href: '/notifications', label: 'Notifications' },
] as const;

function MobileNavigation({
  open,
  logout,
  onClose,
}: {
  open: boolean;
  logout: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  return createPortal(
    <dialog
      id="mobile-navigation"
      open
      aria-label="Navigation menu"
      className="fixed inset-0 z-40 m-0 size-full max-h-none max-w-none bg-transparent p-0 lg:hidden"
    >
      <button
        type="button"
        aria-label="Close navigation menu"
        onClick={onClose}
        className="absolute inset-0 size-full bg-foreground/35"
      />
      <section className="absolute inset-y-0 right-0 flex w-[min(20rem,calc(100%-3rem))] flex-col overflow-y-auto bg-card pb-[env(safe-area-inset-bottom)] shadow-2xl ring-1 ring-foreground/10">
        <div className="flex items-center justify-between border-b border-border/70 px-5 py-4">
          <p className="font-semibold text-foreground">Navigate</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation menu"
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <CloseIcon />
          </button>
        </div>
        <nav className="flex flex-1 flex-col p-3" aria-label="Mobile">
          {mobileNavigationLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={onClose}
              className="rounded-lg px-3 py-3 text-base font-medium text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="border-t border-border/70 p-3">
          <button
            type="button"
            onClick={logout}
            className="w-full rounded-lg px-3 py-3 text-left text-base text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            Sign out
          </button>
        </div>
      </section>
    </dialog>,
    document.body,
  );
}

function MenuIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="size-5"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="size-5"
    >
      <path d="m6 6 12 12M18 6 6 18" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4 shrink-0"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

/**
 * The signed-in user's menu: shows their display name (falling back to email,
 * then a generic label until the shared `/api/me` fetch resolves) and holds
 * Sign out. Menu semantics: opening moves focus to the first menu item,
 * ArrowUp/ArrowDown cycle items, and Escape/outside-click close (Escape
 * returns focus to the trigger).
 */
function AccountMenu({ logout }: { logout: () => void }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    function onPointerDown(event: PointerEvent) {
      const container = containerRef.current;
      if (container && event.target instanceof Node && !container.contains(event.target)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const onMenuKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const activeIndex = items.findIndex((item) => item === document.activeElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (activeIndex + step + items.length) % items.length;
    items[nextIndex]?.focus();
  }, []);

  const label = me?.display_name || me?.email || 'Account';
  // The email line only adds information when the button already shows the
  // display name; otherwise the label is the email (or generic) itself.
  let emailLine: ReactNode = null;
  if (me?.email && me.display_name) {
    emailLine = (
      <div className="truncate border-b border-border/60 px-3 py-2 text-xs text-muted-foreground">
        {me.email}
      </div>
    );
  }
  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <span className="max-w-36 truncate">{label}</span>
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-3.5 shrink-0"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Account"
          tabIndex={-1}
          onKeyDown={onMenuKeyDown}
          className="absolute top-full right-0 z-30 mt-1 w-56 rounded-lg border border-border bg-card p-1 shadow-lg"
        >
          {emailLine}
          <button
            type="button"
            role="menuitem"
            onClick={logout}
            className="w-full rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ActiveFilters({
  filters,
  setFilters,
  openSearch,
}: {
  filters: SearchFilters;
  setFilters: (filters: SearchFilters) => void;
  openSearch: () => void;
}) {
  const count = activeFilterCount(filters);
  if (count === 0) return null;
  return (
    <div className="border-t border-border/40">
      <div className="mx-auto flex max-w-5xl items-center gap-2 overflow-x-auto px-4 py-2 sm:px-6">
        <button
          type="button"
          onClick={openSearch}
          className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-sm text-primary"
        >
          {count} {count === 1 ? 'filter' : 'filters'}
        </button>
        {[
          ...filters.sources,
          ...filters.sections,
          ...filters.statuses,
          ...filters.contentTypes,
        ].map((filter) => (
          <span
            key={filter}
            className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-sm text-muted-foreground"
          >
            {filter.replaceAll('_', ' ')}
          </span>
        ))}
        {filters.dateFrom ? (
          <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-sm text-muted-foreground">
            From {filters.dateFrom}
          </span>
        ) : null}
        {filters.dateTo ? (
          <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-sm text-muted-foreground">
            Through {filters.dateTo}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => setFilters(EMPTY_SEARCH_FILTERS)}
          className="shrink-0 rounded-md px-2 py-1 text-sm text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

type PalettePage = 'root' | 'filters';

type PaletteEntry =
  | { type: 'command'; id: string; group: string; label: string; detail: string; run: () => void }
  | { type: 'local'; id: string; group: string; label: string; detail: string; href: string }
  | {
      type: 'result';
      id: string;
      group: string;
      label: string;
      detail: string;
      result: SearchResult;
    }
  | { type: 'ask'; id: string; group: string; label: string; detail: string; run: () => void };

export function CommandPalette({
  open,
  onClose,
  query,
  setQuery,
  filters,
  setFilters,
  localEntries,
  sectionOptions,
}: {
  open: boolean;
  onClose: () => void;
  query: string;
  setQuery: (query: string) => void;
  filters: SearchFilters;
  setFilters: (filters: SearchFilters) => void;
  localEntries: LocalSearchEntry[];
  sectionOptions: SectionOption[];
}) {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState<PalettePage>('root');
  const [selected, setSelected] = useState(0);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [unavailableSources, setUnavailableSources] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageQuery, setPageQuery] = useState('');
  const inputValue = page === 'root' ? query : pageQuery;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
    if (open) window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (!open || page !== 'root' || query.trim().length < 2) {
      setResults([]);
      setUnavailableSources([]);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      void api
        .search(
          {
            q: query,
            kinds: [],
            contentTypes: filters.contentTypes,
            sources: filters.sources,
            statuses: filters.statuses,
            dateFrom: filters.dateFrom ?? undefined,
            dateTo: filters.dateTo ?? undefined,
            limit: 24,
          },
          controller.signal,
        )
        .then((response) => {
          setResults(response.results);
          setUnavailableSources(response.unavailable_sources ?? []);
          return undefined;
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          setError(caught instanceof Error ? caught.message : String(caught));
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [api, filters, open, page, query]);

  const transition = useCallback((nextPage: PalettePage) => {
    setPage(nextPage);
    setPageQuery('');
    setSelected(0);
  }, []);

  const close = useCallback(() => {
    setPage('root');
    setPageQuery('');
    setSelected(0);
    onClose();
  }, [onClose]);

  const entries = useMemo<PaletteEntry[]>(() => {
    if (page !== 'root') return [];
    const commands: PaletteEntry[] = [
      {
        type: 'command',
        id: 'filters',
        group: 'Commands',
        label: 'Filter Almanac…',
        detail:
          activeFilterCount(filters) > 0
            ? `${activeFilterCount(filters)} active`
            : 'Source, status, type, and date',
        run: () => transition('filters'),
      },
    ];
    if (location.pathname !== '/') {
      commands.push({
        type: 'command',
        id: 'all-digests',
        group: 'Commands',
        label: 'Go to all digests',
        detail: 'Browse every available day',
        run: () => {
          close();
          void navigate('/');
        },
      });
    }
    if (activeFilterCount(filters) > 0) {
      commands.push({
        type: 'command',
        id: 'clear-filters',
        group: 'Commands',
        label: 'Clear all filters',
        detail: 'Restore the complete view',
        run: () => setFilters(EMPTY_SEARCH_FILTERS),
      });
    }

    const rankedLocal = localEntries
      .map((entry) => ({ entry, score: rankCandidate(query, entry.label, entry.keywords) }))
      .filter(
        (candidate): candidate is { entry: LocalSearchEntry; score: number } =>
          candidate.score !== null,
      )
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map<PaletteEntry>(({ entry }) => ({
        type: 'local',
        id: `local:${entry.id}`,
        group: 'This page',
        label: entry.label,
        detail: entry.description,
        href: entry.href,
      }));

    const localIds = new Set(localEntries.map((entry) => entry.id));
    const global = results
      .filter((result) => !localIds.has(result.id))
      .slice(0, 12)
      .map<PaletteEntry>((result) => ({
        type: 'result',
        id: `result:${result.id}`,
        group: 'Across Almanac',
        label: result.title,
        detail: [result.date, result.sourceType, result.contentType.replaceAll('_', ' ')]
          .filter(Boolean)
          .join(' · '),
        result,
      }));

    const ask: PaletteEntry[] = query.trim()
      ? [
          {
            type: 'ask',
            id: 'ask',
            group: 'Ask Almanac',
            label: `Ask “${query.trim()}”`,
            detail: 'Continue with the AI assistant',
            run: () => {
              close();
              askAlmanacAssistant({ question: query.trim() });
              setQuery('');
            },
          },
        ]
      : [];

    return [...commands, ...rankedLocal, ...global, ...ask];
  }, [
    close,
    filters,
    localEntries,
    location.pathname,
    navigate,
    page,
    query,
    results,
    setFilters,
    setQuery,
    transition,
  ]);

  useEffect(() => setSelected(0), [entries.length, page, query]);

  function runEntry(entry: PaletteEntry) {
    if (entry.type === 'command' || entry.type === 'ask') {
      entry.run();
      return;
    }
    close();
    const href = entry.type === 'local' ? entry.href : entry.result.href;
    if (href.startsWith('https://')) {
      window.location.assign(href);
      return;
    }
    void navigate(href);
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((index) => Math.min(index + 1, entries.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Enter' && entries[selected]) {
      event.preventDefault();
      runEntry(entries[selected]);
      return;
    }
    if (event.key === 'Backspace' && !inputValue && page !== 'root') {
      event.preventDefault();
      transition('root');
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (page !== 'root') transition('root');
      else close();
    }
  }

  let previousGroup = '';
  return (
    <dialog
      ref={dialogRef}
      onCancel={(event) => {
        event.preventDefault();
        if (page !== 'root') transition('root');
        else close();
      }}
      onClose={onClose}
      className="m-auto w-[min(44rem,calc(100%-2rem))] rounded-xl bg-popover p-0 text-popover-foreground shadow-2xl ring-1 ring-foreground/10 backdrop:bg-foreground/25 backdrop:backdrop-blur-sm dark:shadow-none"
    >
      <div className="flex items-center gap-3 border-b border-border/70 px-4">
        {page !== 'root' ? (
          <button
            type="button"
            onClick={() => transition('root')}
            className="relative shrink-0 rounded-md px-1.5 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Back to search"
          >
            Back
            <span
              className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
              aria-hidden="true"
            />
          </button>
        ) : null}
        <input
          ref={inputRef}
          type="search"
          name="almanac-search"
          aria-label={page === 'root' ? 'Search Almanac' : 'Filter options'}
          aria-controls={page === 'root' ? 'almanac-search-results' : undefined}
          aria-activedescendant={entries[selected] ? `palette-entry-${selected}` : undefined}
          value={inputValue}
          onChange={(event) => {
            if (page === 'root') setQuery(event.target.value);
            else setPageQuery(event.target.value);
          }}
          onKeyDown={onInputKeyDown}
          placeholder={
            page === 'root' ? 'Search Almanac or ask a question…' : 'Type to narrow options…'
          }
          className="min-w-0 flex-1 bg-transparent py-4 text-base text-foreground outline-none placeholder:text-muted-foreground"
        />
        {loading ? <span className="text-sm text-muted-foreground">Searching…</span> : null}
        <button
          type="button"
          onClick={close}
          className="relative shrink-0 rounded-md px-1.5 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          Close
          <span
            className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
            aria-hidden="true"
          />
        </button>
      </div>
      {page === 'filters' ? (
        <FilterPage
          query={pageQuery}
          filters={filters}
          setFilters={setFilters}
          sectionOptions={sectionOptions}
        />
      ) : null}
      {page === 'root' ? (
        <div id="almanac-search-results" className="max-h-[min(68dvh,36rem)] overflow-y-auto p-2">
          {unavailableSources.includes('wiki') ? (
            <output className="block p-3 text-sm text-muted-foreground">
              Shared knowledge is temporarily unavailable. Personal content search is still
              available.
            </output>
          ) : null}
          {error ? (
            <p className="p-3 text-base text-danger sm:text-sm">Search failed. {error}</p>
          ) : null}
          {entries.map((entry, index) => {
            const showGroup = entry.group !== previousGroup;
            previousGroup = entry.group;
            return (
              <div key={entry.id}>
                {showGroup ? (
                  <div className="px-3 pt-3 pb-1 font-mono text-[0.6875rem] tracking-wide text-muted-foreground uppercase">
                    {entry.group}
                  </div>
                ) : null}
                <button
                  type="button"
                  id={`palette-entry-${index}`}
                  data-selected={selected === index ? '' : undefined}
                  onMouseEnter={() => setSelected(index)}
                  onClick={() => runEntry(entry)}
                  className="flex w-full items-start justify-between gap-4 rounded-lg px-3 py-2.5 text-left data-selected:bg-muted hover:bg-muted"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-base font-medium text-foreground sm:text-sm">
                      {entry.label}
                    </span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {entry.detail}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm text-muted-foreground">Enter</span>
                </button>
              </div>
            );
          })}
          {!loading && entries.length === 0 ? (
            <p className="p-6 text-center text-base text-muted-foreground sm:text-sm">
              No matching commands or content.
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="flex items-center justify-between border-t border-border/70 px-4 py-2 text-sm text-muted-foreground max-sm:hidden">
        <span>Arrow keys to navigate</span>
        <span>Esc to go back</span>
      </div>
    </dialog>
  );
}

const SOURCE_OPTIONS = ['slack', 'gmail', 'linear', 'calendar', 'granola', 'sentry'];
const STATUS_OPTIONS: SearchStatus[] = ['active', 'done', 'snoozed', 'not_relevant'];
const TYPE_OPTIONS: Array<{ value: SearchContentType; label: string }> = [
  { value: 'item', label: 'Items' },
  { value: 'markdown', label: 'Digest notes' },
  { value: 'note', label: 'Personal notes' },
  { value: 'todo', label: 'To-dos' },
  { value: 'research', label: 'Research and findings' },
  { value: 'topic', label: 'Topics' },
  { value: 'doc', label: 'Explainer docs' },
  { value: 'package', label: 'Feature packages' },
  { value: 'timeline', label: 'Timelines' },
  { value: 'metric_grid', label: 'Metrics' },
  { value: 'table', label: 'Tables' },
  { value: 'link_list', label: 'Links' },
];

function toggle<T extends string>(values: T[], value: T): T[] {
  if (values.includes(value)) return values.filter((candidate) => candidate !== value);
  return [...values, value];
}

function FilterPage({
  query,
  filters,
  setFilters,
  sectionOptions,
}: {
  query: string;
  filters: SearchFilters;
  setFilters: (filters: SearchFilters) => void;
  sectionOptions: SectionOption[];
}) {
  const matches = (label: string) => !query || rankCandidate(query, label) !== null;
  return (
    <div className="max-h-[min(68dvh,36rem)] space-y-5 overflow-y-auto p-4">
      <FilterGroup title="Sources">
        {SOURCE_OPTIONS.filter(matches).map((source) => (
          <FilterToggle
            key={source}
            label={source}
            active={filters.sources.includes(source)}
            onClick={() => setFilters({ ...filters, sources: toggle(filters.sources, source) })}
          />
        ))}
      </FilterGroup>
      <FilterGroup title="Status">
        {STATUS_OPTIONS.filter(matches).map((status) => (
          <FilterToggle
            key={status}
            label={status.replaceAll('_', ' ')}
            active={filters.statuses.includes(status)}
            onClick={() => setFilters({ ...filters, statuses: toggle(filters.statuses, status) })}
          />
        ))}
      </FilterGroup>
      <FilterGroup title="Content type">
        {TYPE_OPTIONS.filter((option) => matches(option.label)).map((option) => (
          <FilterToggle
            key={option.value}
            label={option.label}
            active={filters.contentTypes.includes(option.value)}
            onClick={() =>
              setFilters({
                ...filters,
                contentTypes: toggle(filters.contentTypes, option.value),
              })
            }
          />
        ))}
      </FilterGroup>
      {sectionOptions.length > 0 ? (
        <FilterGroup title="This digest">
          {sectionOptions
            .filter((option) => matches(option.label))
            .map((option) => (
              <FilterToggle
                key={option.id}
                label={option.label}
                active={filters.sections.includes(option.id)}
                onClick={() =>
                  setFilters({ ...filters, sections: toggle(filters.sections, option.id) })
                }
              />
            ))}
        </FilterGroup>
      ) : null}
      <fieldset className="space-y-2">
        <legend className="font-mono text-[0.6875rem] tracking-wide text-muted-foreground uppercase">
          Date range
        </legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-base text-muted-foreground sm:text-sm">
            <span>From</span>
            <input
              type="date"
              name="search-date-from"
              value={filters.dateFrom ?? ''}
              onChange={(event) => setFilters({ ...filters, dateFrom: event.target.value || null })}
              className="w-full rounded-lg bg-background px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-ring sm:py-1.5 sm:text-sm"
            />
          </label>
          <label className="space-y-1 text-base text-muted-foreground sm:text-sm">
            <span>To</span>
            <input
              type="date"
              name="search-date-to"
              value={filters.dateTo ?? ''}
              onChange={(event) => setFilters({ ...filters, dateTo: event.target.value || null })}
              className="w-full rounded-lg bg-background px-3 py-2.5 text-base text-foreground ring-1 ring-foreground/10 focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-ring sm:py-1.5 sm:text-sm"
            />
          </label>
        </div>
      </fieldset>
      {activeFilterCount(filters) > 0 ? (
        <button
          type="button"
          onClick={() => setFilters(EMPTY_SEARCH_FILTERS)}
          className="rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          Clear all filters
        </button>
      ) : null}
    </div>
  );
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-2">
      <legend className="font-mono text-[0.6875rem] tracking-wide text-muted-foreground uppercase">
        {title}
      </legend>
      <div className="flex flex-wrap gap-2">{children}</div>
    </fieldset>
  );
}

function FilterToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className="rounded-full bg-muted px-3 py-2 text-base text-muted-foreground aria-pressed:bg-primary/15 aria-pressed:text-primary hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:py-1.5 sm:text-sm"
    >
      {label}
    </button>
  );
}
